import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Save, NotebookText, Trash2, Clock, LogIn } from "lucide-react";
import { toast } from "sonner";
import { AllergiesField } from "@/components/AllergiesField";

function formatDuration(seconds) {
  if (!seconds || seconds < 60) return `${seconds || 0}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const FIELDS = [
  { key: "name", label: "Name", type: "text" },
  { key: "email", label: "Email", type: "email" },
  { key: "mobile", label: "Mobile number", type: "tel" },
  { key: "age", label: "Age", type: "number" },
  { key: "gender", label: "Gender", type: "select", options: ["Male", "Female"] },
  { key: "height_cm", label: "Height (cm)", type: "number" },
  { key: "weight_kg", label: "Weight (kg)", type: "number" },
  { key: "next_followup", label: "Next follow-up", type: "date" },
];

export default function PatientDetail() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [profile, setProfile] = useState({});
  const [logs, setLogs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: l }, { data: pl }, { data: ss }] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/meal-logs?patient_id=${id}`),
        api.get(`/diet-plans?patient_id=${id}`),
        api.get(`/patients/${id}/sessions`),
      ]);
      setPatient(p);
      setProfile(p.profile || {});
      setLogs(l);
      setPlans(pl);
      setSessions(ss);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (k, v) => setProfile((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      ["age", "height_cm", "weight_kg"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      await api.put(`/patients/${id}/profile`, payload);
      toast.success("Profile saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Delete this patient permanently?")) return;
    try {
      await api.delete(`/patients/${id}`);
      toast.success("Patient deleted");
      navigate("/patients");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (!patient) return null;

  return (
    <div className="space-y-6" data-testid="patient-detail-page">
      <Link to="/patients" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-patients">
        <ArrowLeft size={16} /> Back to patients
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-bold text-xl">
            {patient.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold">{patient.name}</h1>
            <p className="text-muted-foreground text-sm">{patient.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate(`/diet-plans/new?patient=${id}`)} className="gap-2" data-testid="new-plan-for-patient">
            <NotebookText size={16} /> New diet plan
          </Button>
          <Button variant="outline" onClick={handleDelete} className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10" data-testid="delete-patient">
            <Trash2 size={16} /> Delete
          </Button>
        </div>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList data-testid="patient-tabs">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Diet plans</TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tab-tracking">Tracking</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <div className="nt-card p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.key} className="space-y-2">
                  <Label htmlFor={f.key}>{f.label}</Label>
                  {f.type === "select" ? (
                    <select
                      id={f.key}
                      value={profile[f.key] ?? ""}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      data-testid={`profile-${f.key}`}
                    >
                      <option value="">— Select —</option>
                      {f.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id={f.key}
                      type={f.type}
                      value={profile[f.key] ?? ""}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      data-testid={`profile-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label htmlFor="medical_conditions">Medical conditions</Label>
              <Textarea id="medical_conditions" value={profile.medical_conditions ?? ""} onChange={(e) => handleChange("medical_conditions", e.target.value)} data-testid="profile-medical_conditions" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="allergies">Allergies</Label>
              <AllergiesField value={profile.allergies ?? ""} onChange={(v) => handleChange("allergies", v)} testIdPrefix="profile-allergies" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="food_preferences">Food preferences</Label>
              <Textarea id="food_preferences" value={profile.food_preferences ?? ""} onChange={(e) => handleChange("food_preferences", e.target.value)} data-testid="profile-food_preferences" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" value={profile.notes ?? ""} onChange={(e) => handleChange("notes", e.target.value)} data-testid="profile-notes" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-profile-button">
              <Save size={16} /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="plans" className="mt-6">
          <div className="nt-card divide-y divide-border">
            {plans.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No diet plans yet</div>
            ) : (
              plans.map((p) => (
                <button
                  key={p.plan_id}
                  onClick={() => navigate(`/diet-plans/${p.plan_id}`)}
                  className="w-full text-left p-5 hover:bg-muted transition-colors flex items-center justify-between"
                  data-testid={`plan-row-${p.plan_id}`}
                >
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {p.plan_type} plan • {p.days?.length || 0} days {p.active && "• Active"}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{p.created_at?.slice(0, 10)}</span>
                </button>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="tracking" className="mt-6">
          <div className="nt-card divide-y divide-border">
            {logs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No meal logs yet</div>
            ) : (
              logs.map((l) => (
                <div key={l.log_id} className="p-5" data-testid={`log-row-${l.log_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="capitalize font-semibold">{l.meal_type}</div>
                    <span className={`text-xs px-2 py-1 rounded-full ${l.status === "completed" ? "bg-secondary text-secondary-foreground" : l.status === "skipped" ? "bg-muted" : "bg-muted"}`}>
                      {l.status}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{l.date} {l.time && `• ${l.time}`}</div>
                  {l.actual_food && <div className="mt-2 text-sm"><span className="font-medium">Ate:</span> {l.actual_food}</div>}
                  {l.notes && <div className="mt-1 text-sm text-muted-foreground">{l.notes}</div>}
                </div>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="sessions" className="mt-6">
          <div className="space-y-4" data-testid="sessions-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="nt-stat" data-testid="sessions-total-logins">
                <span className="nt-label">Total logins</span>
                <div className="text-3xl font-bold">{sessions?.total_logins ?? 0}</div>
              </div>
              <div className="nt-stat" data-testid="sessions-total-time">
                <span className="nt-label">Time on app</span>
                <div className="text-3xl font-bold">{formatDuration(sessions?.total_seconds || 0)}</div>
              </div>
              <div className="nt-stat" data-testid="sessions-last-login">
                <span className="nt-label">Last login</span>
                <div className="text-lg font-semibold mt-1">{sessions?.sessions?.[0] ? formatDateTime(sessions.sessions[0].login_at) : "—"}</div>
              </div>
            </div>

            <div className="nt-card overflow-hidden">
              {!sessions || sessions.sessions.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground">
                  <LogIn size={28} className="mx-auto mb-3 text-primary" />
                  <p className="font-semibold">No logins yet</p>
                  <p className="text-sm mt-1">Session history will appear here after the patient signs in.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="sessions-table">
                    <thead className="bg-muted text-left">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Login</th>
                        <th className="px-4 py-3 font-semibold">Last active</th>
                        <th className="px-4 py-3 font-semibold">Duration</th>
                        <th className="px-4 py-3 font-semibold">Method</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {sessions.sessions.map((s) => (
                        <tr key={s.session_id} data-testid={`session-row-${s.session_id}`}>
                          <td className="px-4 py-3 font-medium">{formatDateTime(s.login_at)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{formatDateTime(s.last_active_at)}</td>
                          <td className="px-4 py-3 inline-flex items-center gap-1.5"><Clock size={12} className="text-primary" />{formatDuration(s.duration_seconds)}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs px-2 py-1 rounded-full bg-secondary text-secondary-foreground capitalize">{s.method}</span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{s.logout_at ? "Signed out" : "Active"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
